import { useState, useEffect, useRef, useCallback } from "react";
import ForceGraph from "./components/ForceGraph";
import Sidebar from "./components/Sidebar";
import DetailPanel from "./components/DetailPanel";
import Tooltip from "./components/Tooltip";
import { useNetworkData } from "./hooks/useNetworkData";
import { THEME } from "./utils/theme";

/**
 * NetworkMap — main container component.
 *
 * Props:
 *   useSampleData   - boolean, use built-in demo data (default: true)
 *   onNavigate      - (node) => void, optional callback for CRM navigation
 *   currencySymbol  - string, default "\u00a3"
 *   className       - optional CSS class for the outermost container
 *   style           - optional inline styles to merge with the container
 */
export default function NetworkMap({
  useSampleData = true,
  onNavigate,
  currencySymbol = "\u00a3",
  className,
  style: containerStyle,
}) {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 900, height: 600 });
  const [hoveredNode, setHoveredNode] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    sector: true, company: true, opportunity: true, contact: true,
    ownership: true, sector_link: true, involvement: true,
    affiliation: true, referral: true, shared: true,
  });

  const { data, loading, error } = useNetworkData({ useSampleData });

  // ── Resize observer ──
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDimensions({ width, height });
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // ── Filtered data ──
  const filteredData = useCallback(() => {
    const matchesSearch = (n) =>
      !searchTerm || n.label.toLowerCase().includes(searchTerm.toLowerCase());

    const nodes = data.nodes.filter((n) => filters[n.type] && matchesSearch(n));
    const nodeIds = new Set(nodes.map((n) => n.id));

    const links = data.links.filter((l) => {
      const key = l.type === "sector" ? "sector_link" : l.type;
      const sid = typeof l.source === "object" ? l.source.id : l.source;
      const tid = typeof l.target === "object" ? l.target.id : l.target;
      return filters[key] && nodeIds.has(sid) && nodeIds.has(tid);
    });

    return {
      nodes: nodes.map((n) => ({ ...n })),
      links: links.map((l) => ({ ...l })),
    };
  }, [data, filters, searchTerm]);

  const { nodes, links } = filteredData();

  const toggleFilter = (key) => setFilters((f) => ({ ...f, [key]: !f[key] }));

  const handleNodeHover = (node, pos, moveOnly) => {
    if (moveOnly) {
      setTooltipPos(pos);
      return;
    }
    setHoveredNode(node);
    if (pos) setTooltipPos(pos);
  };

  const handleNodeClick = (node) => {
    setSelectedNode((prev) => (prev?.id === node.id ? null : node));
  };

  return (
    <div
      className={className}
      style={{
        width: "100%", height: "100%",
        background: THEME.bg, display: "flex",
        fontFamily: "'DM Sans', sans-serif",
        color: THEME.text, overflow: "hidden",
        position: "relative",
        ...containerStyle,
      }}
    >
      <Sidebar
        filters={filters}
        onToggle={toggleFilter}
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        nodeCount={data.nodes.length}
        linkCount={data.links.length}
        allNodes={data.nodes}
      />

      <div ref={containerRef} style={{ flex: 1, position: "relative", overflow: "hidden" }}>
        {loading && (
          <div style={styles.overlay}>
            <div style={styles.spinner} />
            <span style={{ color: THEME.textSecondary, fontSize: 13 }}>Loading network data\u2026</span>
          </div>
        )}

        {error && !loading && (
          <div style={styles.overlay}>
            <span style={{ color: "#EF4444", fontSize: 13 }}>Failed to load: {error}</span>
          </div>
        )}

        {!loading && nodes.length === 0 && (
          <div style={styles.overlay}>
            <span style={{ color: THEME.textSecondary, fontSize: 13 }}>No nodes match current filters</span>
          </div>
        )}

        {!loading && nodes.length > 0 && (
          <ForceGraph
            nodes={nodes}
            links={links}
            width={dimensions.width}
            height={dimensions.height}
            onNodeHover={handleNodeHover}
            onNodeClick={handleNodeClick}
            onNodeRoute={onNavigate}
          />
        )}

        <Tooltip node={hoveredNode} x={tooltipPos.x} y={tooltipPos.y} currencySymbol={currencySymbol} />

        <DetailPanel
          node={selectedNode}
          allLinks={data.links}
          allNodes={data.nodes}
          onClose={() => setSelectedNode(null)}
          onNavigate={onNavigate}
          currencySymbol={currencySymbol}
        />

        {/* Hint bar */}
        <div style={styles.hint}>
          Scroll to zoom {"\u00b7"} Drag to pan {"\u00b7"} Click for details
          {onNavigate && " \u00b7 Double-click to open"}
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "absolute", inset: 0,
    display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center", gap: 12,
  },
  spinner: {
    width: 28, height: 28,
    border: `3px solid ${THEME.border}`,
    borderTopColor: THEME.accent,
    borderRadius: "50%",
    animation: "nmspin 0.8s linear infinite",
  },
  hint: {
    position: "absolute", bottom: 14, left: 14,
    fontSize: 10, color: THEME.textDim,
    background: THEME.surface + "cc",
    padding: "4px 10px", borderRadius: 4,
  },
};
